/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Dialog, Button, Image, Anchor, WarningPopUp } from "hesus-ui-kit";
import PulseLoader from "react-spinners/PulseLoader";
import ClipLoader from "react-spinners/ClipLoader";
import axios from "axios";
import * as moment from "moment";
import { handleApiError } from "@utils/errorHandler";
import closeIcon from "@icons/ic-close-white.svg";
import { getDateFormat } from "@utils/datePickerUtils";
import {
  renderPriceWithCurrencyWithoutUnit,
  renderPriceWithCurrency,
} from "@utils/utils";
import { ColumnButton } from "@utils/extra-style";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";

import {
  Title,
  HeaderWrapper,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  LineItemsGrid,
  LineItemsHeaderCell,
  NoLineItemsMessage,
  JobsiteRow,
  GridRow,
  GridCell,
  LineItemsHeaderRow,
  SectionTitle,
  POInfoRow,
  FlexEndButtonWrapper,
  FlexStartButtonWrapper,
  ApiErrorText,
} from "./PurchaseOrderView.styled";

const PurchaseOrderView = (props) => {
  const {
    t,
    id,
    reference,
    onClose,
    updateRefreshStatus,
    editNewPurchaseOrder,
  } = props;

  const generalParams = useGetGeneralParameters();
  const { countryCurrencyPosition } = generalParams;
  const currencyList = generalParams.countryCurrency;

  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [purchaseOrderId, setPurchaseOrderId] = useState("");
  const [isConfirmVisible, setConfirmVisibility] = useState(false);
  const [isConfirm, setActionConfirmStatus] = useState(false);
  const [actionType, setActionType] = useState("");

  const [isConfirmVisibleSend, setConfirmVisibilitySend] = useState(false);
  const [isConfirmSend, setActionConfirmStatusSend] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);
  const [freeItemArray, setFreeItemArray] = useState([]);
  const [groupedByQuoteItems, setGroupedByQuoteItems] = useState({});
  const [data, setData] = useState([]);
  const colorArray = { 0: "#ED7D1B", 1: "#32A72C", 2: "#949494", 3: "#949494" };
  const backgroundColor = {
    0: "rgba(237, 125, 27, 0.15)",
    1: "rgba(50, 167, 44, 0.15)",
    2: "rgba(148, 148, 148, 0.15)",
    3: "rgba(148, 148, 148, 0.15)",
  };

  const statusText = {
    0: "PURCHASE_ORDER_STATUS_PENDING",
    1: "PURCHASE_ORDER_STATUS_SENT",
    2: "PURCHASE_ORDER_STATUS_CANCELLED",
    3: "PURCHASE_ORDER_STATUS_DELETED",
  };

  const processData = (po = []) => {
    const { items = [], ...poData } = po;
    const groupedPoItems = {};
    const itemsWithoutQuoteItem = [];
    items.forEach((item) => {
      if (item.quoteItem) {
        const groupId = item?.jobsite?.id+"_"+item.quoteService?.id;
        if (!groupedPoItems[groupId]) {
          groupedPoItems[groupId] = [];
        }
        groupedPoItems[groupId].push(item);
      } else {
        itemsWithoutQuoteItem.push(item);
      }
    });
    return { poData, groupedPoItems, itemsWithoutQuoteItem };
  };

  const fetchPurchaseOrder = () => {
    setIsLoading(true);
    axios
      .get(`/purchaseOrders/${id}`)
      .then((response) => {
        const { poData, groupedPoItems, itemsWithoutQuoteItem } = processData(
          response.data?.purchaseOrders?.[0]
        );
        setFreeItemArray(itemsWithoutQuoteItem);
        setGroupedByQuoteItems(groupedPoItems);
        setData(poData);
      })
      .catch((error) => {
        setApiError(
          error.response?.data?.message ?? "Failed to load purchase order"
        );
      })
      .finally(() => setIsLoading(false));
  };
  useEffect(() => {
    fetchPurchaseOrder();
  }, []);

  const getColumn = () => {
    let counter = 2;
    if (data.status === 1) counter += 1;
    if (data.supplierQuoteNumber) counter += 1;
    return `repeat(${counter}, minmax(0, auto))`;
  };

  const updateStatus = (purchaseOrderId) => {
    setApiError("");
    axios
      .put(`/purchaseOrders/${purchaseOrderId}`, {
        id: purchaseOrderId,
        status: 1,
      })
      .then(() => {
        setIsDeleting(false);
        setPurchaseOrderId("");
        setPurchaseOrderId("");
        updateRefreshStatus();
      })
      .catch((error) => {
       setIsDeleting(false);
       setPurchaseOrderId("");
       setPurchaseOrderId("");
        setApiError(
          error.response?.data?.message ?? "Failed to save purchase order"
        );
      });
  };

  useEffect(() => {
    if (isConfirm) {
      setIsDeleting(true);
      const request =
        actionType === "delete"
          ? axios.delete(`/purchaseOrders/${purchaseOrderId}`)
          : axios.get(`/purchaseOrders/${purchaseOrderId}/generatePdf`);
      request
        .then(() => {
          if (actionType === "delete") updateRefreshStatus();
          if (actionType === "send") updateStatus(purchaseOrderId);
          setIsDeleting(false);
          setPurchaseOrderId("");
          setPurchaseOrderId("");
        })
        .catch((error) => {
          setIsDeleting(false);
          handleApiError(error);
          setPurchaseOrderId("");
          setPurchaseOrderId("");
        })
        .finally(() => {
          setIsDeleting(false);
          setPurchaseOrderId("");
          setPurchaseOrderId("");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirm]);

  const deletePurchaseOrder = (id) => {
    setActionType("delete");
    setPurchaseOrderId(id);
    setConfirmVisibility(true);
  };

  const onPopUpCancel = () => {
    setPurchaseOrderId("");
    setActionType("");
    setConfirmVisibility(false);
    setActionConfirmStatus(false);
  };

  const onPopUpConfirm = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(true);
  };

  const previewPurchaseOrder = () => {
    console.log("");
  };

  const sendPurchaseOrder = (id) => {
    setActionType("send");
    setPurchaseOrderId(id);
    setConfirmVisibility(true);
  };

  return (
    <Dialog width="65rem">
      <div>
        <HeaderWrapper>
          <Title>{t("PURCHASE_ORDER_POPUP_TITLE") + " #" + reference}</Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close meeting report popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        {isLoading && (
          <div className="suggestions-list-loading">
            <PulseLoader color="#1bace4" size={15} />
          </div>
        )}
        <FormBody>
          <OptionsWrapper>
            {data?.id && (
              <FlexRow>
                <SectionContainer>
                  <GridRow column="1fr auto" padding="0px 16px 5px">
                    <SectionTitle
                      margin="0px"
                      borderBottom="0"
                      paddingBottom="0"
                    >
                      {t("PURCHASE_ORDER_INFORMATION")}
                    </SectionTitle>
                    <GridCell>
                      <ColumnButton
                        border={backgroundColor[data.status]}
                        background={backgroundColor[data.status]}
                        color={colorArray[data.status]}
                        marginRight="10px"
                        borderRadius="20px"
                      >
                        {t(statusText[data.status])}
                      </ColumnButton>
                    </GridCell>
                  </GridRow>
                  <GridRow column="28% 45% 1fr" border="0">
                    <POInfoRow>
                      <GridCell
                        fontSize="14px"
                        color="#00AEEA"
                        fontWeight="700"
                      >
                        {t("SUPPLIER")}
                      </GridCell>
                      <GridCell>
                        <Anchor
                          url={`/company/view/${data.supplier.id}/general-info`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {data.supplier.name}
                        </Anchor>
                      </GridCell>
                    </POInfoRow>
                    <POInfoRow>
                      <GridCell
                        fontSize="14px"
                        color="#00AEEA"
                        fontWeight="700"
                      >
                        {t("CONTACT")}
                      </GridCell>
                      <GridCell>
                        {data.contacts?.map((contact) => {
                          return (
                            <ColumnButton
                              border="#00AEEA26"
                              background="#00AEEA26"
                              color="#0A4986"
                              marginRight="10px"
                              borderRadius="20px"
                              marginTop="1px"
                              marginBottom="1px"
                              key={
                                "contact_id" +
                                contact.contactId +
                                "-" +
                                contact.id
                              }
                            >
                              {contact.firstName + " " + contact.lastName}
                            </ColumnButton>
                          );
                        })}
                      </GridCell>
                    </POInfoRow>
                    <POInfoRow>
                      <GridCell
                        fontSize="14px"
                        color="#00AEEA"
                        fontWeight="700"
                      >
                        {t("PURCHASE_ORDER_ORDER_DATE")}
                      </GridCell>
                      <GridCell>
                        {data.orderDate
                          ? getDateFormat(data.orderDate, "YYYY-MM-DD")
                          : ""}
                      </GridCell>
                    </POInfoRow>
                  </GridRow>
                  <GridRow column={getColumn} border="0">
                    <POInfoRow>
                      <GridCell
                        fontSize="14px"
                        color="#00AEEA"
                        fontWeight="700"
                      >
                        {t("PURCHASE_ORDER_HESUS_CONTACT")}
                      </GridCell>
                      <GridCell>
                        {data.hesusContact.firstName +
                          " " +
                          data.hesusContact.lastName}
                      </GridCell>
                    </POInfoRow>
                    <POInfoRow>
                      <GridCell
                        fontSize="14px"
                        color="#00AEEA"
                        fontWeight="700"
                      >
                        {t("PURCHASE_ORDER_PAYMENT_TERMS")}
                      </GridCell>
                      <GridCell>{data.paymentTerm.label}</GridCell>
                    </POInfoRow>
                    {data.supplierQuoteNumber && (
                      <POInfoRow>
                        <GridCell
                          fontSize="14px"
                          color="#00AEEA"
                          fontWeight="700"
                        >
                          {t("PURCHASE_ORDER_QUOTE_NUMBER")}
                        </GridCell>
                        <GridCell>{data.supplierQuoteNumber}</GridCell>
                      </POInfoRow>
                    )}
                    {data.status === 1 && (
                      <POInfoRow>
                        <GridCell
                          fontSize="14px"
                          color="#00AEEA"
                          fontWeight="700"
                        >
                          {t("PURCHASE_ORDER_SENT_AT")}
                        </GridCell>
                        <GridCell>
                          {data.sentAt
                            ? getDateFormat(data.sentAt, "YYYY-MM-DD") +
                              " " +
                              t("AT") +
                              " " +
                              moment(data.sentAt).format("HH:mm")
                            : ""}
                        </GridCell>
                      </POInfoRow>
                    )}
                  </GridRow>
                  {data.comment && (
                    <GridRow column="1fr" border="0">
                      <POInfoRow>
                        <GridCell
                          fontSize="14px"
                          color="#00AEEA"
                          fontWeight="700"
                        >
                          {t("PURCHASE_ORDER_COMMENT")}
                        </GridCell>
                        <JobsiteRow backgroundColor="#F5F5F5" clor="#474747">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: data.comment,
                            }}
                          ></div>
                        </JobsiteRow>
                      </POInfoRow>
                    </GridRow>
                  )}
                  <SectionTitle margin="10px 0 10px 0">
                    {t("PURCHASE_ORDER_ITEMS")}
                  </SectionTitle>
                  <LineItemsGrid>
                    <LineItemsHeaderRow>
                      <LineItemsHeaderCell>
                        {t("PURCHASE_ORDER_HESUS_SERVICE")}
                      </LineItemsHeaderCell>
                      <LineItemsHeaderCell>{t("LABEL")}</LineItemsHeaderCell>
                      <LineItemsHeaderCell>{t("PRODUCT")}</LineItemsHeaderCell>
                      <LineItemsHeaderCell>
                        {t("TRUCK_TYPE")}
                      </LineItemsHeaderCell>
                      <LineItemsHeaderCell>{t("QUANTITY")}</LineItemsHeaderCell>
                      <LineItemsHeaderCell>
                        {t("UNIT_PRICE")}
                      </LineItemsHeaderCell>
                      <LineItemsHeaderCell>{t("UNIT")}</LineItemsHeaderCell>
                    </LineItemsHeaderRow>
                    {Object.entries(groupedByQuoteItems).length === 0 && (
                      <NoLineItemsMessage>
                        {t("PURCHASE_ORDER_NO_LINE_ITEMS")}
                      </NoLineItemsMessage>
                    )}
                    {Object.entries(groupedByQuoteItems).length > 0 &&
                      Object.entries(groupedByQuoteItems).map(
                        ([groupId, items]) => (
                          <React.Fragment key={groupId}>
                            <JobsiteRow>
                              #
                              {items[0]?.jobsite?.opportunityNumber ??
                                items[0]?.jobsite?.id ??
                                ""}{" "}
                              - {items[0]?.jobsite?.name ?? ""} /{" "}
                              {items[0]?.quoteService?.label ?? ""}
                            </JobsiteRow>

                            {items.map((item) => (
                              <React.Fragment key={item.id}>
                                <GridRow
                                  border={
                                    item.comment
                                      ? "0px solid #e5e5e5"
                                      : "1px solid #e5e5e5"
                                  }
                                >
                                  <GridCell fontWeight="700">
                                    {item.quoteItem?.hesusService?.name}
                                    <GridCell
                                      fontWeight="400"
                                      fontStyle="italic"
                                      fontSize="13px"
                                      color="#767676"
                                    >
                                      {renderPriceWithCurrency(
                                        item.quoteItem.itemSellingPrice,
                                        t(item.unit),
                                        item.quoteService?.currency?.symbol,
                                        item.quoteService?.currency?.position
                                      )}
                                    </GridCell>
                                  </GridCell>
                                  <GridCell>{item.label}</GridCell>
                                  <GridCell>
                                    {item.quoteItem?.product?.name ??
                                      item.product}
                                  </GridCell>
                                  <GridCell>
                                    {item.quoteItem?.transport?.name ??
                                      item.transportType}
                                  </GridCell>
                                  <GridCell textAlign="center">
                                    {item.quantity}
                                  </GridCell>
                                  <GridCell textAlign="right">
                                    {renderPriceWithCurrencyWithoutUnit(
                                      item.unitPrice,
                                      currencyList[data.country.id],
                                      countryCurrencyPosition[data.country.id]
                                    )}
                                  </GridCell>
                                  <GridCell textAlign="center">
                                    {t(item.unit)}
                                  </GridCell>
                                </GridRow>
                                {item.comment && (
                                  <GridRow
                                    column="auto 1fr"
                                    padding="6px 16px 0px"
                                    gap="0px"
                                  >
                                    <GridCell> </GridCell>
                                    <GridCell>
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: item.comment,
                                        }}
                                      ></div>
                                    </GridCell>
                                  </GridRow>
                                )}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        )
                      )}
                    {freeItemArray.length > 0 && (
                      <>
                        <JobsiteRow backgroundColor="rgba(240, 128, 70, 0.2)">
                          {" "}
                          {t("PURCHASE_ORDER_FREE_LINES")}
                        </JobsiteRow>
                        {freeItemArray.map((item) => (
                          <React.Fragment key={item.id}>
                            <GridRow
                              border={
                                item.comment
                                  ? "0px solid #e5e5e5"
                                  : "1px solid #e5e5e5"
                              }
                            >
                              <GridCell>-</GridCell>
                              <GridCell>{item.label}</GridCell>
                              <GridCell>{item.product}</GridCell>
                              <GridCell>{item.transportType}</GridCell>
                              <GridCell textAlign="center">
                                {item.quantity}
                              </GridCell>
                              <GridCell textAlign="right">
                                {renderPriceWithCurrencyWithoutUnit(
                                  item.unitPrice,
                                  currencyList[data.country.id],
                                  countryCurrencyPosition[data.country.id]
                                )}
                              </GridCell>
                              <GridCell textAlign="center">
                                {t(item.unit)}
                              </GridCell>
                            </GridRow>
                            {item.comment && (
                              <GridRow
                                column="auto 1fr"
                                padding="6px 16px 0px"
                                gap="0px"
                              >
                                <GridCell> </GridCell>
                                <GridCell>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: item.comment,
                                    }}
                                  ></div>
                                </GridCell>
                              </GridRow>
                            )}
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </LineItemsGrid>
                </SectionContainer>
              </FlexRow>
            )}
          </OptionsWrapper>
        </FormBody>
        <ButtonWrapper>
          {apiError && <ApiErrorText>{apiError}</ApiErrorText>}
          {[0, 1].includes(parseInt(data?.status, 10)) && (
            <FlexStartButtonWrapper>
              <Button
                styles={{
                  color: "#fff",
                  background: "#E83F1E",
                  border: "1px solid #E83F1E",
                  marginRight: "0.32rem",
                  "&:hover": {
                    backgroundColor: "#E83F1E",
                  },
                }}
                onClick={() => deletePurchaseOrder(data.id)}
              >
                <>
                  {isDeleting && actionType === "delete" && (
                    <ClipLoader size={15} color="#fff" />
                  )}
                  {t("GENERIC_ACTION_DELETE")}
                </>
              </Button>
            </FlexStartButtonWrapper>
          )}
          <FlexEndButtonWrapper>
            {[0, 1].includes(parseInt(data?.status, 10)) && (
              <Button
                styles={{
                  color: "#fff",
                  background: "#0A4986",
                  border: "1px solid #0A4986",
                  marginRight: "0.32rem",
                  "&:hover": {
                    backgroundColor: "#022d58",
                  },
                }}
                onClick={previewPurchaseOrder}
              >
                <>{t("PURCHASE_ORDER_ACTION_PREVIEW")}</>
              </Button>
            )}
            {[0].includes(parseInt(data?.status, 10)) && (
              <>
                <Button
                  styles={{
                    color: "white",
                    background: "#1BACE4",
                    border: "1px solid #1BACE4",
                    marginRight: "0.32rem",
                    "&:hover": {
                      backgroundColor: "#1589b6",
                    },
                  }}
                  onClick={editNewPurchaseOrder}
                >
                  <>{t("GENERIC_ACTION_EDIT")}</>
                </Button>
                <Button
                  styles={{
                    color: "white",
                    background: "#008395",
                    border: "1px solid #008395",
                    marginRight: "0.32rem",
                    "&:hover": {
                      backgroundColor: "#01545f",
                    },
                  }}
                  onClick={() => sendPurchaseOrder(data.id)}
                >
                  <>
                    {isDeleting && actionType === "send" && (
                      <ClipLoader size={15} color="#fff" />
                    )}
                    {t("PURCHASE_ORDER_ACTION_SEND")}
                  </>
                </Button>
              </>
            )}
          </FlexEndButtonWrapper>
        </ButtonWrapper>
        {isConfirmVisible && (
          <WarningPopUp
            title={
              actionType === "send"
                ? t("PURCHASE_ORDER_CONFIRMATION_BOX_SEND")
                : t("PURCHASE_ORDER_CONFIRMATION_BOX_DELETE")
            }
            message={
              actionType === "send"
                ? t("PURCHASE_ORDER_CONFIRMATION_BOX_SEND_CONTENT")
                : t("PURCHASE_ORDER_CONFIRMATION_BOX_DELETE_CONTENT")
            }
            onPopUpCancel={onPopUpCancel}
            onPopUpConfirm={onPopUpConfirm}
            ValidateContent={t("YES")}
            CancelContent={t("CANCEL")}
          />
        )}
      </div>
    </Dialog>
  );
};

PurchaseOrderView.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(PurchaseOrderView);
